import { useCallback, useRef, useState } from "react";
import { Box, Button, darken, lighten, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Alert, palette, SimpleDialog, useInterval, useSnackbar } from "@powerdiary/components-library/lib";
import { CompositionExposed } from "common.ui/CustomForms/Editor";
import { IComposition, ICustomFormEditorParams } from "common.ui/CustomForms/Editor/DataTypes/IStore";
import { ProfileFieldType as CommonUiProfileFieldType } from "common.ui/CustomForms/Editor/DataTypes/field";
import { FormState, LockStatus } from "../../Form.types";
import { useSaveForm } from "../../hooks/useSaveForm";
import { Composition, CustomFormModel, CustomFormViewModel, DeleteFileRequest } from "types/generated/models";
import { useRemoveFile } from "../../hooks/useRemoveFile";
import Content from "../../../Layout/Content";
import { useClientPortalNavigate } from "../../../../hooks/useClientPortalRouter";
import { useInitializeStripe } from "../../../Shared/Stripe";
import { useClientPortalConfigContext } from "../../../../context/ClientPortalConfig.ctx";
import StripeProvider from "../../../Shared/Stripe/StripeProvider";
import { convertToCompositionParams } from "../CustomForm.helper";
import SaveCreditCardDialog, { SaveCreditCardDialogState } from "../SaveCreditCardDialog/SaveCreditCardDialog";
import { ROUTES } from "../../../../routes";
import { getUploadResponseError } from "../../../../utils/uploadError";

interface IFormEditProps {
    formData: CustomFormViewModel;
    sgName: string;
    formName: string;
    customFormModel: CustomFormModel;
    publicId: string;
}

const MAX_MOBILE_PHONE_LENGTH = 16;

const cleanTelephoneNumber = (value: string): string => value.replaceAll(/[^0-9+]/g, "");

const hasTooLongMobilePhone = (composition: IComposition | null | undefined): boolean => {
    for (const section of composition?.sections ?? []) {
        for (const field of section.fields ?? []) {
            if (field.profileFieldType !== CommonUiProfileFieldType.MobilePhone) {
                continue;
            }

            const normalizedValue = cleanTelephoneNumber(String(field.text ?? ""));
            if (normalizedValue.length > MAX_MOBILE_PHONE_LENGTH) {
                return true;
            }
        }
    }

    return false;
};

export default function FormEdit({ formData, sgName, formName, customFormModel, publicId }: IFormEditProps) {
    const [formState, setFormState] = useState<FormState>(FormState.Dirty);
    const [status, setStatus] = useState<LockStatus>(formData?.status ?? LockStatus.Draft);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [saveCreditCardDialogState, setSaveCreditCardDialogState] = useState<SaveCreditCardDialogState>({
        isOpen: false,
        updateCallback: null,
    });
    const compositionInstance = useRef<CompositionExposed | null>(null);
    const isSavingRef = useRef(false);
    const isSubmittingRef = useRef(false);
    const activeSavePromiseRef = useRef<Promise<boolean> | null>(null);
    const busyUploadersRef = useRef<Set<string>>(new Set());
    const [isUploading, setIsUploading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { saveForm } = useSaveForm();
    const { removeFile } = useRemoveFile();
    const navigate = useClientPortalNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const { clientPortalConfig } = useClientPortalConfigContext();
    const { stripe, loading: stripeLoading } = useInitializeStripe({
        stripeKey: clientPortalConfig?.stripeKey || "",
        practitionerStripeAccountId: formData.clientStripeAccount || undefined,
    });
    const { clientPortalGuid } = useClientPortalConfigContext();

    const compositionParams = convertToCompositionParams(customFormModel, publicId, clientPortalGuid);

    const getFormData = useCallback(
        (lockStatus: LockStatus): CustomFormViewModel => {
            const composition = compositionInstance.current?.getEntryComposition();
            return {
                id: compositionParams.templateId,
                composition: composition as unknown as Composition,
                status: lockStatus,
            };
        },
        [compositionParams.templateId, compositionInstance]
    );

    const performSave = useCallback(
        async (lockStatus: LockStatus): Promise<boolean> => {
            if (isSavingRef.current && activeSavePromiseRef.current !== null) {
                return await activeSavePromiseRef.current;
            }

            isSavingRef.current = true;
            setIsSaving(true);
            setFormState(FormState.Saving);

            const promise = (async () => {
                try {
                    compositionInstance.current?.flushAllPendingUpdates();
                    const data = getFormData(lockStatus);
                    await saveForm(data);

                    setFormState(FormState.Saved);
                    setStatus(lockStatus);
                    return true;
                } catch {
                    setFormState(FormState.Dirty);
                    return false;
                } finally {
                    isSavingRef.current = false;
                    setIsSaving(false);
                    activeSavePromiseRef.current = null;
                }
            })();

            activeSavePromiseRef.current = promise;
            return await promise;
        },
        [getFormData, saveForm]
    );

    const autoSave = useCallback(async () => {
        if (
            formState === FormState.Dirty &&
            status !== LockStatus.Locked &&
            !showConfirmDialog &&
            !isSavingRef.current
        ) {
            const instance = compositionInstance.current;
            if (instance && hasTooLongMobilePhone(instance.getEntryComposition())) return;

            await performSave(LockStatus.AutoDraft);
        }
    }, [formState, status, showConfirmDialog, performSave]);

    useInterval(
        () => {
            void autoSave();
        },
        status !== LockStatus.Locked && !showConfirmDialog ? 5000 : null
    );

    // Tracks which client-file uploaders are mid-upload (key = `${sectionId}-${fieldId}`) so the
    // form cannot be submitted while a file is still uploading. Submitting mid-upload locked the
    // form against a composition snapshot that omitted the in-flight file, orphaning it out of the
    // client attachments.
    const handleUploaderBusyChanged = useCallback((key: string, isBusy: boolean) => {
        const busyUploaders = busyUploadersRef.current;
        if (isBusy) {
            busyUploaders.add(key);
        } else {
            busyUploaders.delete(key);
        }
        setIsUploading(busyUploaders.size > 0);
    }, []);

    const waitForUploadsToFinish = useCallback((timeoutMs = 30000): Promise<void> => {
        if (busyUploadersRef.current.size === 0) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            const startedAt = Date.now();
            const intervalId = setInterval(() => {
                if (busyUploadersRef.current.size === 0 || Date.now() - startedAt > timeoutMs) {
                    clearInterval(intervalId);
                    resolve();
                }
            }, 100);
        });
    }, []);

    const handleCompositionChange = useCallback(() => {
        if (status !== LockStatus.Locked) {
            setFormState(FormState.Dirty);
        }
    }, [status]);

    const handleRemoveFile = useCallback(
        async (fileId: number) => {
            const data = getFormData(status);
            await removeFile({ fileId, customFormId: data.id, publicId } as DeleteFileRequest);
            await autoSave();
        },
        [getFormData, status, removeFile, autoSave, publicId]
    );

    const handleFinish = useCallback(async () => {
        if (!compositionInstance.current) {
            console.warn("Composition instance not ready");
            return;
        }

        const isValid = await compositionInstance.current.runValidation();

        if (!isValid) {
            enqueueSnackbar("Your form could not be submitted. Please fix any errors and try again.", {
                variant: "error",
            });
            return;
        }

        setShowConfirmDialog(true);
    }, [enqueueSnackbar, compositionInstance]);

    const handleConfirmSubmit = useCallback(async () => {
        if (isSubmittingRef.current) {
            return;
        }

        isSubmittingRef.current = true;
        setIsSubmitting(true);

        await waitForUploadsToFinish();

        if (activeSavePromiseRef.current !== null) {
            await activeSavePromiseRef.current;
        }

        const success = await performSave(LockStatus.Locked);

        if (success) {
            navigate(ROUTES.FORM_SUBMISSION_SUCCESS.urlPath);
            return;
        }

        isSubmittingRef.current = false;
        setIsSubmitting(false);
    }, [performSave, navigate, waitForUploadsToFinish]);

    const handleCancelSubmit = useCallback(() => {
        setShowConfirmDialog(false);
    }, []);

    const showErrorToUser = useCallback(
        (message: string) => {
            enqueueSnackbar(message, { variant: "error" });
        },
        [enqueueSnackbar]
    );

    const showStripeModal = useCallback(
        (updateFieldCallback: (value: string) => void) => {
            if (!formData.clientStripeAccount) {
                enqueueSnackbar("Unable to add card details. Please contact the clinic for assistance.", {
                    variant: "error",
                });
                return;
            }

            setSaveCreditCardDialogState({
                isOpen: true,
                updateCallback: updateFieldCallback,
            });
        },
        [formData.clientStripeAccount, enqueueSnackbar]
    );

    const handleCardSaved = useCallback(
        (value: string) => {
            if (saveCreditCardDialogState.updateCallback) {
                saveCreditCardDialogState.updateCallback(value);
            }
            setSaveCreditCardDialogState({ isOpen: false, updateCallback: null });
        },
        [saveCreditCardDialogState]
    );

    const handleStripeModalClose = useCallback(() => {
        setSaveCreditCardDialogState({ isOpen: false, updateCallback: null });
    }, []);

    const getFormStateDisplayName = () => {
        switch (formState) {
            case FormState.Saved:
                return "Saved";
            case FormState.Saving:
                return "Saving";
            default:
                return "";
        }
    };

    const initialParams: ICustomFormEditorParams = {
        ...compositionParams,
        isPortalCustomFormView: true,
        displayModes: false,
        onContentChange: handleCompositionChange,
        removeClientFile: handleRemoveFile,
        getUploadError: getUploadResponseError,
        onRemoveClientFile: () => {
            void autoSave();
        },
        uppyColorConfig: {
            main: theme.palette.primary.main,
            light: lighten(theme.palette.primary.main, 0.9),
            dark: darken(theme.palette.primary.main, 0.3),
        },
        onClientFileAdded: autoSave,
        onUploaderBusyChanged: handleUploaderBusyChanged,
        showErrorToUser: showErrorToUser,
        showStripeModal: showStripeModal,
        isEditable: false,
        templateName: "",
        promptUnsavedChanges: false,
        shouldUpdateComposition: false,
        afterSaveCallback: () => {},
        editCustomFormAct: () => {},
        expiryPeriod: 0,
        isShownOnClientPortal: false,
        showFormsOnClientPortal: false,
        previewClientFile: () => {},
        clientFileDownloadUrl: "",
        isCreditCardFieldEnabled: false,
        hideDeprecatedGenderOptions: false,
        renderFroalaInIFrame: false,
        getDrawingsUrl: "",
        uploadDrawing: "",
        drawingSizeLimit: 0,
        updateDrawingUrl: "",
        backToTemplatesUrl: "",
        mergeFieldInfoUrl: "",
        saveTemplateUrl: "",
        isImportPreview: false,
        localization: { state: "" },
        notification: { hasSupplier: false, emails: "" },
        labels: {},
        isCustomFormTemplateEditView: false,
        isClientCustomFormEditView: false,
        updateDrawingDataUrl: () => {},
        defaultFieldTitle: "",
        deleteTemplateSettings: {
            enableDelete: false,
            deleteTemplateUrl: "",
            listTemplateUrl: "",
            validationUrl: "",
            restoreTemplateUrl: "",
            isActive: false,
        },
        scoring: {
            isCustomFormsScoringEnabled: false,
            isScoreEnabled: false,
            displayScoresInPortal: false,
            assessmentScores: [],
        },
    };

    if (status === LockStatus.Locked) {
        return (
            <Box textAlign="center" mt={5}>
                <Typography variant="body1">Thank you. We have received your form.</Typography>
            </Box>
        );
    }

    return (
        <Content dataTestIdSuffix="custom_form_edit">
            <Stack spacing={2} p={isMobile ? 2 : 4}>
                <Typography variant="paragraphLargeBold" color={palette.neutral[900]}>
                    {formName}
                </Typography>

                <Paper>
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <Stack spacing={2} p={isMobile ? 2 : 5} width={"100%"}>
                            <Alert variant="hint">
                                Please complete the form below and click the Finish button when it is complete. Your
                                data is automatically saved as you go.
                            </Alert>

                            <CompositionExposed ref={compositionInstance} params={initialParams} />

                            <Stack
                                spacing={2}
                                direction="row"
                                sx={{
                                    alignSelf: "flex-end",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <Typography variant="paragraphMediumRegular">{getFormStateDisplayName()}</Typography>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        void handleFinish();
                                    }}
                                    disabled={isSaving || isSubmitting || isUploading}
                                >
                                    {isUploading ? "Uploading..." : isSaving || isSubmitting ? "Processing" : "Finish"}
                                </Button>
                            </Stack>
                        </Stack>

                        <SimpleDialog
                            open={showConfirmDialog}
                            handleClose={isSubmitting ? undefined : handleCancelSubmit}
                            dialogTitleText="Confirmation"
                            dialogContentText={`You are about to submit this form to ${sgName}. You will not be able to edit it after it has been submitted.`}
                            renderActions={() => (
                                <>
                                    <Button
                                        variant="outlined"
                                        size="medium"
                                        onClick={handleCancelSubmit}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        onClick={() => {
                                            void handleConfirmSubmit();
                                        }}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "Submitting..." : "Yes"}
                                    </Button>
                                </>
                            )}
                        />
                    </Box>
                </Paper>
            </Stack>

            {!stripeLoading && stripe && saveCreditCardDialogState.isOpen && (
                <StripeProvider stripe={stripe}>
                    <SaveCreditCardDialog
                        open={saveCreditCardDialogState.isOpen}
                        onClose={handleStripeModalClose}
                        publicId={publicId || ""}
                        practitionerId={formData.primaryPractitionerId || undefined}
                        onCardSaved={handleCardSaved}
                    />
                </StripeProvider>
            )}
        </Content>
    );
}
